import { ACQUIRE_ERROR_LABELS_PL, type AcquireErrorCode } from "@anisync/types";
import { RotateCwIcon, TriangleAlertIcon } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import type { PlayAttempts } from "../lib/api";

/**
 * Widok porażki z tabelą prób.
 *
 * Pomiary pokazały, że martwy odnośnik jest w tym ekosystemie stanem normalnym —
 * dwie trzecie odnośników z jednego serwisu było martwych, a odnośniki
 * bezpośrednie u innego martwe w całości. Samo „nie udało się" nie pozwoliłoby
 * ani zrozumieć problemu, ani go zgłosić, więc pokazujemy KAŻDĄ próbę: skąd
 * braliśmy, jaką metodą i co odpowiedział host.
 */
export function FailureState({
  attempts,
  retryable,
  busy,
  onRetry,
  errorCode = null,
}: {
  attempts: PlayAttempts | null;
  retryable: boolean;
  busy: boolean;
  onRetry: (preferHost?: string) => void;
  errorCode?: string | null;
}) {
  const rows = attempts?.attempts ?? [];

  /**
   * Wszystkie kopie odrzucone przyciskiem „Zła wersja”. Osobny widok, bo ogólny
   * tekst o martwych odnośnikach byłby tu nieprawdą, a „Spróbuj ponownie”
   * prowadziłoby w kółko do tej samej odmowy. Jedyną drogą jest przywrócenie.
   */
  if (errorCode === "ALL_REJECTED") {
    return (
      <div className="flex flex-col gap-3 rounded-xl border border-warning/40 bg-warning/5 p-8">
        <p className="flex items-center gap-2 text-sm font-medium tracking-[0.2em] text-warning uppercase">
          <TriangleAlertIcon className="size-4" />
          Brak dobrej kopii
        </p>
        <h2 className="text-2xl font-bold">Wszystkie kopie tego odcinka oznaczono jako złe</h2>
        <p className="max-w-2xl text-muted-foreground">
          Automat nie pobiera kopii odrzuconych przyciskiem „Zła wersja”. Jeśli któraś była
          oznaczona przez pomyłkę, przywróć ją w oknie wersji na stronie serii — albo poczekaj, aż
          źródła dodadzą nową.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 rounded-xl border border-destructive/40 bg-destructive/5 p-8">
      <div className="flex flex-col gap-2">
        <p className="flex items-center gap-2 text-sm font-medium tracking-[0.2em] text-destructive uppercase">
          <TriangleAlertIcon className="size-4" />
          Niepowodzenie
        </p>
        <h2 className="text-2xl font-bold">Nie udało się pobrać tego odcinka</h2>
        <p className="max-w-2xl text-muted-foreground">
          Sprawdziliśmy {rows.length === 1 ? "jedno źródło" : `${rows.length} źródeł`}. Martwe
          odnośniki są tu zjawiskiem częstym — serwisy trzymają adresy, z których materiał już
          zniknął. Możesz spróbować ponownie albo wskazać konkretny odtwarzacz.
        </p>
      </div>

      {rows.length > 0 && (
        <Table className="min-w-[36rem]">
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Host</TableHead>
              <TableHead>Metoda</TableHead>
              <TableHead>Wynik</TableHead>
              <TableHead>Czas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((a) => (
              <TableRow key={`${a.index}-${a.host}`}>
                <TableCell className="text-muted-foreground tabular-nums">{a.index}</TableCell>
                <TableCell className="font-medium">
                  {a.host}
                  {/* Grupa ze źródła — przy kilku kopiach na jednym hoście to
                      jedyne, co je odróżnia. Starsze wpisy pola nie mają. */}
                  {a.label !== undefined && (
                    <span className="block text-xs font-normal text-muted-foreground">
                      {a.label}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {a.method}
                  {a.viaEgress && <span className="text-warning"> · tunel</span>}
                </TableCell>
                <TableCell>
                  {a.ok ? (
                    <Badge variant="success">pobrano</Badge>
                  ) : (
                    <span className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="destructive">
                        {a.errorCode !== undefined
                          ? ACQUIRE_ERROR_LABELS_PL[a.errorCode as AcquireErrorCode]
                          : "nieznany błąd"}
                      </Badge>
                      {a.httpStatus !== undefined && (
                        <span className="text-xs text-muted-foreground">HTTP {a.httpStatus}</span>
                      )}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground tabular-nums">
                  {a.durationMs !== undefined ? formatDuration(a.durationMs) : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <div className="flex flex-wrap gap-3">
        <Button variant="hero" size="lg" disabled={busy || !retryable} onClick={() => onRetry()}>
          <RotateCwIcon />
          {busy ? "Próbuję…" : "Spróbuj ponownie"}
        </Button>

        {/* Wskazanie hosta ma sens tylko wtedy, gdy jakiś w ogóle odpowiedział —
            inaczej podpowiadalibyśmy wybór, który już raz zawiódł tak samo. */}
        {rows
          .filter((a) => a.errorCode !== "NOT_FOUND")
          .map((a) => (
            <Button
              key={`retry-${a.index}`}
              variant="outline"
              size="lg"
              disabled={busy}
              onClick={() => onRetry(a.host)}
            >
              Wymuś {a.host}
            </Button>
          ))}
      </div>
    </div>
  );
}

/**
 * Czas próby w formie znośnej do przeczytania.
 *
 * Milisekundy są właściwe przy błędzie po sekundzie, ale przy pobraniu, które
 * trwało dwanaście minut, „730133 ms" nie mówi nic — a to realna wartość
 * z produkcji.
 */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes} min ${seconds} s`;
}
